import isAfter from 'date-fns/isAfter'
import {Formik} from 'formik'
import React, {FunctionComponent, useMemo, useRef, useState} from 'react'
import {SearchIcon} from 'styles/inline-icons'
import {AnimateHeightOnlyMobile} from 'util/animateheight'
import {style} from 'util/campagne/style'
import {dateFromServerDate} from 'util/dates'
import {useFormikChange} from 'util/formikchanges'
import {useTranslation} from 'util/i18n'
import {Theme} from 'views/campagne/theme'
import {ButtonPrimary} from 'views/ui/Button'
import {fetch} from 'yetch'
import {
	DISPLAY_DATE_FORMAT,
	filterEvents,
	Filters
} from '../../../../util/filteritems'
import {CtaRegister, CtaRegisterType} from '../ctaregister'
import {DatePicker} from '../datepicker'
import {Dropdown, DropdownItem} from '../dropdown'
import {FormField, FormfieldCheckbox} from '../formfield'
import {Prediction, QueryPlaces} from '../forms/queryplaces'
import {ActionCard, ActionCardFullImage, ActionCardType} from './actioncard'
import './actionsoverview.scss'

type Categories = Array<{title: string; path: string; id: number}>

export type ActionsOverviewType = {
	actions: Array<ActionCardType>
	cta: CtaRegisterType
	categories: Categories
}

const styles = style('actionsoverview', ['filters', 'container'])
const dropdownOptions = [
	{key: '1', value: 1},
	{key: '5', value: 5},
	{key: '15', value: 15},
	{key: '25', value: 25},
	{key: '40', value: 40},
	{key: '80', value: 80},
	{key: '120', value: 120},
	{key: '160', value: 160},
	{key: '200', value: 200}
]

type ActionsFilterType = {
	filter: (filters: Filters) => void
	categories: Categories
	values: typeof defaultValues
}

const ITEMS_PER_PAGE = 9

export const ActionsOverview: FunctionComponent<ActionsOverviewType> = ({
	actions,
	cta,
	categories
}) => {
	const t = useTranslation('campagne').actions
	const [useNumberOfItems, setNumberOfItems] = useState(ITEMS_PER_PAGE)
	const [filtered, setFiltered] = useState(actions)
	const events = useMemo(() => {
		const res = actions.slice()
		return res.sort((a, b) => {
			const dateA = dateFromServerDate(a.date_start)
			const dateB = dateFromServerDate(b.date_start)
			const currentDate = new Date()
			if (!dateA || !dateB) return 0
			const aHasPassed = isAfter(currentDate, dateA)
			const bHasPassed = isAfter(currentDate, dateB)
			if (aHasPassed && !bHasPassed) return 1
			if (bHasPassed && !aHasPassed) return -1
			return dateA.getTime() - dateB.getTime()
		})
	}, [actions])
	const filter = (filters: Filters) =>
		setFiltered(filterEvents(filters, events))

	return (
		<Formik initialValues={defaultValues} onSubmit={(values) => filter(values)}>
			{({setValues, values}) => {
				return (
					<div class={styles}>
						<div class={styles('filters')}>
							<Theme.Container mod="shifttoright">
								<ActionsFilter
									categories={categories}
									filter={filter}
									values={values}
								/>
							</Theme.Container>
						</div>
						<div class={styles('overview')}>
							<Theme.Container mod="shifttoright">
								<div class={styles('title')}>
									<Theme.H3 mod={{slantedtoleft: true, white: true}}>
										{t.title}
									</Theme.H3>
								</div>
								<ActionsContainer>
									<div class={styles('overview-card').mod('big')}>
										{filtered.length > 0 ? (
											filtered[0] && (
												<ActionCardFullImage mod={'big'} {...filtered[0]} />
											)
										) : (
											<div class={styles('overview-noresults')}>
												<Theme.P>{t.no_results}</Theme.P>
												<div class={styles('overview-noresults-button')}>
													<Theme.Button
														onClick={() => setValues(defaultValues)}
													>
														{t.clearfilters}
													</Theme.Button>
												</div>
											</div>
										)}
									</div>
									<div class={styles('overview-card').mod('cta')}>
										<CtaRegister mod="greyraster" {...cta} />
									</div>
									{filtered.length > 0 &&
										filtered.slice(1, useNumberOfItems).map((action, i) => {
											if (i === 0 || i === 4) {
												return (
													<div
														class={styles('overview-card').mod({wide: i === 4})}
													>
														<ActionCardFullImage
															mod={i === 0 ? 'square' : null}
															{...action}
														/>
													</div>
												)
											}
											return (
												<div class={styles('overview-card')}>
													<ActionCard {...action} />
												</div>
											)
										})}
								</ActionsContainer>
								<div class={styles('overview-button')}>
									{filtered && filtered.length > useNumberOfItems && (
										<ButtonPrimary
											onClick={() =>
												setNumberOfItems(useNumberOfItems + ITEMS_PER_PAGE)
											}
										>
											{t.loadmore}
										</ButtonPrimary>
									)}
								</div>
							</Theme.Container>
						</div>
					</div>
				)
			}}
		</Formik>
	)
}

const ActionsContainer: FunctionComponent = ({children}) => (
	<div class={styles.container}>{children}</div>
)

const FilterResults = ({filter}) => {
	const geometry = useRef()
	useFormikChange(({values}) => {
		if (values.location)
			fetch(`/api/places/${values.location}`)
				.then((res) => res.json())
				.then((place) => (geometry.current = place.result.geometry.location))
				.then(() => filter({...values, geometry: geometry.current}))
		else geometry.current = null
		filter({...values, geometry: geometry.current})
	})
	return null
}

const defaultValues = {
	location: null,
	radius: '15',
	keyword: null,
	date_type: 'on' as 'on' | 'between',
	date_start: null,
	date_end: null
}

export const ActionsFilter: FunctionComponent<ActionsFilterType> = ({
	filter,
	categories,
	values
}) => {
	const t = useTranslation('campagne').filters
	const dateTypeOptions = [
		{key: 'on', value: t.on},
		{key: 'between', value: t.between}
	]
	const valueDateType = values.date_type

	return (
		<form autoComplete="off" class={styles.filters}>
			<FilterResults filter={filter} />
			<AnimateHeightOnlyMobile title={'Filter acties'} animateOpacity>
				<div class={styles.filters('left')}>
					<div class={styles.filters('left-top')}>
						<div class={styles.filters('left-item')}>
							<div class={styles.filters('label')}>{t.location}</div>
							<Dropdown<Prediction>
								autocomplete
								name="location"
								placeholder="gemeente, postcode, ..."
								itemToString={(candidate) =>
									candidate && candidate.structured_formatting.main_text
								}
								itemToKey={(candidate) => candidate && candidate.place_id}
							>
								{(query) => <QueryPlaces query={query} />}
							</Dropdown>
						</div>
						<div class={styles.filters('left-item').mod('radius')}>
							<div class={styles.filters('label')}>{t.radius}</div>
							<Dropdown
								itemToKey={(item) => item.key}
								name="radius"
								placeholder={`${defaultValues.radius} km`}
								itemToString={(item: typeof dropdownOptions[0]): string => {
									if (!item || !item.value) return ''
									return item.value.toString() + ' km'
								}}
							>
								{dropdownOptions.map((item, index) => (
									<DropdownItem item={item} small index={index}>
										{item.value + ' km'}
									</DropdownItem>
								))}
							</Dropdown>
						</div>
						<div class={styles.filters('left-item')}>
							<FormField
								icon={<SearchIcon />}
								name="keyword"
								label={t.keyword}
								labelFlex="column"
								id="keyword"
								placeholder="vb. Straatfeest"
								small
							/>
						</div>
					</div>
					<div class={styles.filters('left-bottom')}>
						<label
							htmlFor={'date_start'}
							class={styles.filters('left-bottom-label')}
						>
							{t.periode}
						</label>
						<div className={styles.filters('left-bottom-datetype')}>
							<Dropdown
								placeholder={t.on}
								name="date_type"
								itemToKey={(item: typeof dateTypeOptions[0]): string =>
									item?.key
								}
								itemToString={(item: typeof dateTypeOptions[0]): string => {
									if (!item || !item.value) return ''
									return item.value.toString()
								}}
							>
								{dateTypeOptions.map((item, index) => (
									<DropdownItem item={item} small index={index}>
										{item.value}
									</DropdownItem>
								))}
							</Dropdown>
						</div>
						<div class={styles.filters('left-bottom-datepicker')}>
							<DatePicker
								placeholder={'vb. 20/10'}
								name="date_start"
								toFormat={DISPLAY_DATE_FORMAT}
							/>
						</div>
						{valueDateType === 'between' && (
							<>
								<label
									htmlFor={'date_end'}
									class={styles.filters('left-bottom-label').mod('small')}
								>
									{t.and}
								</label>
								<div class={styles.filters('left-bottom-datepicker')}>
									<DatePicker
										placeholder={'vb. 20/10'}
										name="date_end"
										toFormat={DISPLAY_DATE_FORMAT}
									/>
								</div>
							</>
						)}
					</div>
				</div>

				<div class={styles.filters('right')}>
					<span class={styles.filters('right-title')}>{t.filtertitle}</span>
					<div class={styles.filters('right-container')}>
						{categories.map((category) => (
							<div class={styles.filters('right-container-filter')}>
								<FormfieldCheckbox small name={`category_${category.id}`}>
									<span class={styles.filters('right-container-filter-label')}>
										{category.title}
									</span>
								</FormfieldCheckbox>
							</div>
						))}
					</div>
				</div>
			</AnimateHeightOnlyMobile>
		</form>
	)
}
